// ═══ THE REQUEST GATE + ONE QUIET RETRY ON AN EXPIRED TOKEN (Philip, 2026-10-10) ═══
// The v2 design from docs/rls-assessment.md §13.5, after the 2026-08-09 attempt (a fetch wrapper that refreshed on
// PGRST303 and signOut()'d on failure) caused a 42501 storm on every page load and was reverted. Its two lessons are
// the two rules here:
//   1. NEVER A COMPETING REFRESH. On an expired-token answer this asks supabase-js for its session (getSession), which
//      refreshes through supabase's OWN lock-serialised, de-duplicated path when the token is due — the same call
//      supabase makes before every request. It never calls refreshSession(). If that yields a NEW token the request
//      is retried once with it; the same token (or none) means nothing to retry with, and the original answer is
//      returned as it came.
//   2. STOP REQUESTS, NEVER MUTATE AUTH. This file never signs anyone out. Once a signed-in session is lost
//      (SIGNED_OUT after one existed), the gate is 'dead' and every data/storage request is answered here with a
//      synthetic 401 SESSION_ENDED BEFORE the network — so nothing goes out on the public key while the sign-in box
//      (App.js) is up. Signing back in reopens it. Auth requests (/auth/v1/) always pass: sign-in and supabase's
//      own refresh must never be gated.
// THE FAILURE PATH IS TESTABLE (§14: test it first). In development only, localStorage 'wh_debug_fail_refresh' = '1'
// makes the token-refresh request fail as the server would (400 refresh_token_not_found) without the network;
// window.__whAuthDebug.refresh() then triggers a refresh on demand. Unit tests: src/__tests__/auth-fetch.test.js.

export const SESSION_ENDED = 'SESSION_ENDED';
const isAuthUrl = (url) => /\/auth\/v1\//.test(url);
const isRefreshUrl = (url) => /\/auth\/v1\/token\?grant_type=refresh_token/.test(url);
const urlOf = (input) => (typeof input === 'string' ? input : (input && input.url) || String(input));
const json = (status, body) => new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });

// An expired access token: PostgREST answers 401 PGRST303; Storage answers with "jwt expired" / "exp" claim text.
async function isExpiredToken(res) {
  if (res.status !== 401 && res.status !== 400 && res.status !== 403) return false;
  try {
    const text = await res.clone().text();
    return /PGRST303/.test(text) || /jwt expired|exp\W+claim/i.test(text);   // \W+: the quotes arrive JSON-escaped
  } catch (e) { return false; }
}

const bearerOf = (init) => {
  const h = new Headers(init && init.headers);
  const a = h.get('Authorization') || '';
  return a.startsWith('Bearer ') ? a.slice(7) : null;
};

/**
 * @param baseFetch   the real fetch
 * @param getSession  () => Promise<session|null> — supabase.auth.getSession's session (supabase's own refresh path)
 * @param debug       () => boolean — the forced-refresh-failure toggle (development only)
 * @returns { fetch, setSignedIn(bool), state() }
 */
export function createAuthFetch({ baseFetch, getSession, debug = () => false }) {
  let state = 'none';   // none (never signed in this load) | ok | dead (a signed-in session was lost)

  async function authFetch(input, init) {
    const url = urlOf(input);
    if (isAuthUrl(url)) {
      if (isRefreshUrl(url) && debug()) {
        return json(400, { code: 400, error_code: 'refresh_token_not_found', msg: 'Forced refresh failure (wh_debug_fail_refresh)' });
      }
      return baseFetch(input, init);
    }
    if (state === 'dead') {
      return json(401, { code: SESSION_ENDED, message: 'Your session has ended — sign in again to continue.' });
    }
    const res = await baseFetch(input, init);
    if (!(await isExpiredToken(res))) return res;
    // Expired token: let supabase refresh its own way, then retry ONCE with the new token — or not at all.
    const used = bearerOf(init);
    let session = null;
    try { session = await getSession(); } catch (e) { session = null; }
    const fresh = session && session.access_token;
    if (!fresh || fresh === used || state === 'dead') {
      console.warn('[authFetch] expired token and no new one to retry with — returning the original answer', { url });
      return res;
    }
    const headers = new Headers(init && init.headers);
    headers.set('Authorization', `Bearer ${fresh}`);
    console.info('[authFetch] expired token — retried once with the refreshed one', { url });
    return baseFetch(input, { ...(init || {}), headers });
  }

  return {
    fetch: authFetch,
    // App wiring: a session present → ok; lost after being ok → dead; never had one → stays none.
    setSignedIn(signedIn) { state = signedIn ? 'ok' : (state === 'none' ? 'none' : 'dead'); },
    state: () => state,
  };
}
